import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthorizedApiService } from '@core';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { ColumnDefinition, MasterTableComponent, TableAction, TableFilterDefinition } from '@shared/components/master-table/master-table.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { SystemLogListResponse, SystemLogRecord } from './system-log.interface';
import { UserConfig } from '../../config/users/user.interface';

@Component({
  selector: 'app-system-log',
  standalone: true,
  imports: [BreadcrumbComponent, MasterTableComponent, TranslateModule],
  templateUrl: './system-log.component.html',
  styleUrl: './system-log.component.scss',
})
export class SystemLogComponent implements OnInit {
  readonly dataSource = new MatTableDataSource<SystemLogRecord>([]);
  readonly actions: TableAction<SystemLogRecord>[] = [{
    id: 'details',
    icon: 'visibility',
    tooltip: 'Ver detalle',
    colorClass: 'info',
    auth: { resource: 'logs', action: 'read' },
    selectionMode: 'single',
  }];

  columnDefinitions: ColumnDefinition[] = [];
  tableFilters: TableFilterDefinition<SystemLogRecord>[] = [];
  activeFilters: Record<string, string | number | boolean | null> = {};
  isLoading = true;
  currentPage = 1;
  pageSize = 50;
  totalItems = 0;
  private loadRequestSequence = 0;
  searchValue = '';

  private readonly api = inject(AuthorizedApiService);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private searchHandle: ReturnType<typeof setTimeout> | null = null;
  private readonly userNames = new Map<string, string>();
  private usersLoaded = false;

  ngOnInit(): void {
    this.configureView();
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.configureView();
      this.dataSource.data = this.dataSource.data.map((row) => this.normalize(row));
    });
    void this.initializeData();
  }

  private async initializeData(): Promise<void> {
    await this.loadUserDirectory();
    this.configureView();
    await this.loadData();
  }

  async loadData(): Promise<void> {
    const requestId = ++this.loadRequestSequence;
    this.isLoading = true;
    try {
      const query = this.searchValue.trim();
      const response = await firstValueFrom(this.api.get<SystemLogListResponse>('logs', {
        params: {
          page: this.currentPage,
          perPage: this.pageSize,
          ...(this.filter('action') ? { action: this.filter('action') } : {}),
          ...(this.filter('model') ? { model: this.filter('model') } : {}),
          ...(query ? { recordId: query } : {}),
          ...(this.filter('actor') ? { uid: this.filter('actor') } : {}),
          ...(this.filter('dateFrom') ? { dateFrom: this.dayBoundary(this.filter('dateFrom'), false) } : {}),
          ...(this.filter('dateTo') ? { dateTo: this.dayBoundary(this.filter('dateTo'), true) } : {}),
        },
      }));

      const rows = response?.data || [];
      await this.resolveFirebaseActors(rows);
      if (requestId !== this.loadRequestSequence) return;
      this.dataSource.data = rows.map((row) => this.normalize(row));
      this.totalItems = response?.pagination?.total || 0;
      this.configureView();
    } catch (error) {
      if (requestId !== this.loadRequestSequence) return;
      console.error('Error loading system audit logs', error);
      this.dataSource.data = [];
      this.totalItems = 0;
      this.snackBar.open(extractApiErrorMessage(error, this.t('systemLogsCatalog.notifications.loadError')), '', {
        duration: 2600,
        verticalPosition: 'bottom',
        horizontalPosition: 'center',
        panelClass: 'snackbar-danger',
      });
    } finally {
      if (requestId === this.loadRequestSequence) this.isLoading = false;
    }
  }

  onSearchChange(value: string): void {
    this.searchValue = value.trim();
    this.currentPage = 1;
    if (this.searchHandle) clearTimeout(this.searchHandle);
    this.searchHandle = setTimeout(() => void this.loadData(), 350);
  }

  onFiltersChange(filters: Record<string, string | number | boolean | null>): void {
    this.activeFilters = filters;
    this.currentPage = 1;
    void this.loadData();
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    void this.loadData();
  }

  viewDetail(row: SystemLogRecord): void {
    void this.router.navigate(['/attendance/system-logs', row.logId], { queryParamsHandling: 'preserve' });
  }

  private configureView(): void {
    this.actions[0].tooltip = this.t('systemLogsCatalog.actions.view');
    this.columnDefinitions = [
      { def: 'select', label: this.t('common.select'), type: 'check', visible: true },
      { def: 'occurredAtLabel', label: this.t('systemLogsCatalog.fields.occurredAt'), type: 'text', visible: true },
      { def: 'actionLabel', label: this.t('systemLogsCatalog.fields.action'), type: 'status', visible: true },
      { def: 'moduleKey', label: this.t('systemLogsCatalog.fields.module'), type: 'text', visible: true },
      { def: 'modelLabel', label: this.t('systemLogsCatalog.fields.model'), type: 'text', visible: true },
      { def: 'recordId', label: this.t('systemLogsCatalog.fields.recordId'), type: 'text', visible: true, tooltip: true },
      { def: 'actorLabel', label: this.t('systemLogsCatalog.fields.actor'), type: 'text', visible: true, tooltip: true },
      { def: 'source', label: this.t('systemLogsCatalog.fields.source'), type: 'text', visible: true },
      { def: 'changesLabel', label: this.t('systemLogsCatalog.fields.changes'), type: 'text', visible: true },
    ];
    this.tableFilters = [
      {
        id: 'action', label: this.t('systemLogsCatalog.filters.action'), icon: 'bolt',
        value: this.activeFilters['action'] ?? null,
        options: ['add', 'update', 'remove', 'create', 'delete', 'read'].map((value) => ({ value, label: this.optionLabel('actions', value) })),
      },
      {
        id: 'model', label: this.t('systemLogsCatalog.filters.model'), icon: 'database', searchable: true,
        value: this.activeFilters['model'] ?? null,
        options: ['employee', 'user', 'companiesRecord', 'orgUnit', 'attendanceConfig', 'attendanceEvent'].map((value) => ({ value, label: value })),
      },
      {
        id: 'actor', label: this.t('systemLogsCatalog.filters.actor'), icon: 'person', searchable: true,
        value: this.activeFilters['actor'] ?? null,
        options: Array.from(this.userNames.entries())
          .map(([uid, name]) => ({ value: uid, label: name, searchText: `${name} ${uid}` }))
          .sort((left, right) => left.label.localeCompare(right.label)),
      },
      { id: 'dateFrom', label: this.t('systemLogsCatalog.filters.dateFrom'), icon: 'calendar_today', type: 'date', value: this.activeFilters['dateFrom'] ?? null },
      { id: 'dateTo', label: this.t('systemLogsCatalog.filters.dateTo'), icon: 'event', type: 'date', value: this.activeFilters['dateTo'] ?? null },
    ];
  }

  private normalize(row: SystemLogRecord): SystemLogRecord {
    const action = row.action || '-';
    return {
      ...row,
      actionLabel: this.optionLabel('actions', action),
      modelLabel: row.model || '-',
      actorLabel: row.actorName
        || (this.isFirebaseActor(row) ? this.t('systemLogsCatalog.unknownUser') : '')
        || (this.isServiceActor(row) ? this.t('systemLogsCatalog.automaticSystem') : '')
        || row.actor?.uid
        || '-',
      changesLabel: this.t('systemLogsCatalog.changeCount', { count: this.changeCount(row.data) }),
      occurredAtLabel: this.formatDate(row.occurredAt),
    };
  }

  private filter(key: string): string {
    return String(this.activeFilters[key] || '');
  }

  private changeCount(data: SystemLogRecord['data']): number {
    if (Array.isArray(data)) return data.length;
    if (data && typeof data === 'object') return Object.keys(data).length;
    return 0;
  }

  private async resolveFirebaseActors(rows: SystemLogRecord[]): Promise<void> {
    const pendingUids = rows
      .filter((row) => this.isFirebaseActor(row) && !!row.actor?.uid && !this.userNames.has(row.actor.uid))
      .map((row) => row.actor!.uid as string);

    if (pendingUids.length) await this.loadUserDirectory();

    for (const row of rows) {
      const uid = row.actor?.uid || '';
      row.actorName = this.isFirebaseActor(row) ? this.userNames.get(uid) || '' : '';
    }
  }

  private async loadUserDirectory(): Promise<void> {
    if (this.usersLoaded) return;
    try {
      const users = await firstValueFrom(this.api.get<UserConfig[]>('users'));
      for (const user of users || []) {
        this.userNames.set(user.uid, user.displayName || user.email || user.uid);
      }
      this.usersLoaded = true;
    } catch (error) {
      console.warn('Could not load users for the audit actor filter', error);
    }
  }

  private isFirebaseActor(row: SystemLogRecord): boolean {
    return (row.actor?.via || '').trim().toLowerCase() === 'firebase';
  }

  private isServiceActor(row: SystemLogRecord): boolean {
    return (row.actor?.via || '').trim().toLowerCase() === 'service';
  }

  private dayBoundary(value: string, end: boolean): string {
    return new Date(`${value}T${end ? '23:59:59.999' : '00:00:00'}`).toISOString();
  }

  private formatDate(value?: string | null): string {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat(this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX', {
      dateStyle: 'medium', timeStyle: 'medium',
    }).format(date);
  }

  private optionLabel(group: string, value: string): string {
    const key = `systemLogsCatalog.${group}.${value}`;
    const label = this.t(key);
    return label === key ? value : label;
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}
